import {
  CircleNotchIcon,
  LinkedinLogoIcon,
  LockIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { Button, Checkbox, Input } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import type { FormEvent } from "react";

type Props = {
  url: string;
  consent: boolean;
  urlValid: boolean;
  busy: boolean;
  error?: string;
  onUrlChange: (value: string) => void;
  onConsentChange: (value: boolean) => void;
  onSubmit: (event: FormEvent) => void;
};

export const LinkedInUrlStep = ({
  url,
  consent,
  urlValid,
  busy,
  error,
  onUrlChange,
  onConsentChange,
  onSubmit,
}: Props) => {
  const hasInvalidUrl = url.trim().length > 0 && !urlValid;

  const canSubmit = urlValid && consent && !busy;

  const focusUrlInput = () => {
    document.getElementById("linkedin-profile-url")?.focus();
  };

  return (
    <section className="mx-auto w-full max-w-2xl py-3 sm:py-6">
      <header>
        <div className="flex items-start gap-3">
          <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-[#0A66C2]/10 text-[#0A66C2]">
            <LinkedinLogoIcon aria-hidden size={24} weight="fill" />
          </span>

          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-600">
              Import rapide
            </p>

            <h1 className="mt-1 text-2xl font-bold tracking-tight text-[#071451] sm:text-3xl">
              Importez votre profil LinkedIn
            </h1>

            <p className="mt-2 max-w-xl text-xs leading-5 text-slate-600 sm:text-sm">
              Transformez les informations de votre profil public en un CV professionnel et
              entièrement modifiable.
            </p>
          </div>
        </div>
      </header>

      <form
        onSubmit={onSubmit}
        noValidate
        className="mt-6 space-y-5 rounded-2xl border border-indigo-100 bg-white p-4 shadow-[0_14px_40px_rgba(30,27,75,0.07)] sm:p-6"
      >
        <button
          type="button"
          onClick={focusUrlInput}
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#0A66C2] px-5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#075aa9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A66C2] focus-visible:ring-offset-2"
        >
          <LinkedinLogoIcon aria-hidden size={20} weight="fill" />
          Continuer avec LinkedIn
        </button>

        <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
          <span className="h-px flex-1 bg-slate-200" />

          <span className="text-center">ou collez votre lien public</span>

          <span className="h-px flex-1 bg-slate-200" />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="linkedin-profile-url"
            className="text-xs font-bold text-[#071451] sm:text-sm"
          >
            URL de votre profil LinkedIn
          </label>

          <div className="relative">
            <LinkedinLogoIcon
              aria-hidden
              size={17}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#0A66C2]"
            />

            <Input
              id="linkedin-profile-url"
              type="url"
              inputMode="url"
              autoComplete="url"
              spellCheck={false}
              value={url}
              disabled={busy}
              placeholder="https://www.linkedin.com/in/votre-profil"
              onChange={(event) => {
                onUrlChange(event.target.value);
              }}
              aria-invalid={hasInvalidUrl}
              aria-describedby={hasInvalidUrl ? "linkedin-url-error" : undefined}
              className={cn(
                "h-12 rounded-xl pl-10 text-xs sm:text-sm",
                hasInvalidUrl && "border-rose-400 focus-visible:ring-rose-200",
              )}
            />
          </div>

          {hasInvalidUrl && (
            <p id="linkedin-url-error" className="flex items-start gap-1.5 text-xs text-rose-600">
              <WarningCircleIcon aria-hidden size={14} weight="fill" className="mt-0.5 shrink-0" />
              Format attendu : https://www.linkedin.com/in/votre-profil
            </p>
          )}
        </div>

        <label
          htmlFor="linkedin-import-consent"
          className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 transition hover:border-indigo-200 hover:bg-indigo-50/40"
        >
          <Checkbox
            id="linkedin-import-consent"
            checked={consent}
            disabled={busy}
            onCheckedChange={(checked) => {
              onConsentChange(checked === true);
            }}
            className="mt-0.5 shrink-0"
          />

          <span className="text-xs leading-5 text-slate-600 sm:text-sm">
            Je confirme qu’il s’agit de{" "}
            <strong className="font-bold text-[#071451]">mon propre profil public</strong> et
            j’autorise MonCVPro à utiliser ses informations pour préremplir mon CV.
          </span>
        </label>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs leading-5 text-rose-700"
          >
            <WarningCircleIcon aria-hidden size={16} weight="fill" className="mt-0.5 shrink-0" />

            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={!canSubmit}
          className="min-h-12 w-full rounded-xl bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
        >
          {busy ? (
            <CircleNotchIcon aria-hidden size={17} className="animate-spin" />
          ) : (
            <LinkedinLogoIcon aria-hidden size={17} weight="fill" />
          )}

          {busy ? "Démarrage de l’import…" : "Importer mon profil"}
        </Button>

        <p className="flex items-start justify-center gap-1.5 text-center text-[10px] leading-4 text-slate-500 sm:text-xs">
          <LockIcon aria-hidden size={13} className="mt-0.5 shrink-0" />
          L’import est gratuit. Le CV ne sera créé qu’après votre confirmation.
        </p>
      </form>
    </section>
  );
};
